import request from '../../../../util/APIUtils';
import { PORTAL_API_BASE_URL } from '../../../../constants';

/**
 * Fetches a single case plan with sections and widgets for the admin detail view.
 * @param {string} casePlanId - UUID of the case plan
 * @returns {Promise<{ plan: object, sections: object[] }>}
 */
export const fetchCasePlanDetail = (casePlanId, signal) =>
  request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}`,
    method: 'GET',
    signal,
  });

/**
 * Queues a case plan export for the selected sections.
 * @param {string} casePlanId - UUID of the case plan
 * @param {{ emails: string[], password: string, sectionIds: string[] }} payload
 */
export const exportCasePlan = (casePlanId, { emails, password, sectionIds }) =>
  request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}/export`,
    method: 'POST',
    body: JSON.stringify({
      emails,
      password,
      sectionIds,
    }),
  });

/**
 * Last-in-wins plan PATCH. Status-only body — do not send updatedAt.
 * @param {string} casePlanId - UUID of the case plan
 * @param {{ status: string }} payload
 */
export const patchCasePlan = (casePlanId, { status }) =>
  request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}`,
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

const buildWidgetUrl = (casePlanId, widgetId) =>
  `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}/widgets/${widgetId}`;

/**
 * Last-in-wins widget PATCH. Description-only bodies skip config validation.
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} widgetId - UUID of the widget
 * @param {{ config?: unknown, description?: string | null }} payload
 */
export const patchCasePlanWidget = (casePlanId, widgetId, { config, description }) =>
  request({
    url: buildWidgetUrl(casePlanId, widgetId),
    method: 'PATCH',
    body: JSON.stringify({
      ...(config !== undefined ? { config } : {}),
      ...(description !== undefined ? { description } : {}),
    }),
  });

const buildEditorsUrl = (casePlanId) =>
  `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}/editors`;

/**
 * Lists active (`is_active = true`) editors for a plan.
 * @param {string} casePlanId - UUID of the case plan
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ editors: object[] }>}
 */
export const fetchCasePlanEditors = (casePlanId, signal) =>
  request({
    url: buildEditorsUrl(casePlanId),
    method: 'GET',
    signal,
  });

/**
 * Replaces the active editor list. Body is the chips still on the modal after local add/remove.
 * @param {string} casePlanId - UUID of the case plan
 * @param {{ editorIds: number[] }} payload
 * @returns {Promise<{ editors: object[] }>}
 */
export const patchCasePlanEditors = (casePlanId, { editorIds }) =>
  request({
    url: buildEditorsUrl(casePlanId),
    method: 'PATCH',
    body: JSON.stringify({ editorIds }),
  });

/**
 * Reorders a widget in its section or moves it to another section.
 * `sortOrder` is the final zero-based active-widget index (`0..n-1` in-section,
 * `0..n` across sections).
 * `sectionId` alone drops the widget at the top of that section.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} widgetId - UUID of the widget
 * @param {{ sectionId?: string, sortOrder?: number }} payload
 * @returns {Promise<object>} Moved widget
 */
export const moveCasePlanWidget = (casePlanId, widgetId, { sectionId, sortOrder } = {}) =>
  request({
    url: `${buildWidgetUrl(casePlanId, widgetId)}/move`,
    method: 'PATCH',
    body: JSON.stringify({
      ...(sectionId !== undefined ? { sectionId } : {}),
      ...(sortOrder !== undefined ? { sortOrder } : {}),
    }),
    suppressErrorToast: true,
  });

/**
 * Soft-deletes or archives a widget depending on plan status. Draft plans delete; Active and
 * Released plans archive.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} widgetId - UUID of the widget
 * @returns {Promise<object>} Updated widget
 */
export const deleteCasePlanWidget = (casePlanId, widgetId) =>
  request({
    url: buildWidgetUrl(casePlanId, widgetId),
    method: 'DELETE',
    suppressErrorToast: true,
  });

const buildSectionsUrl = (casePlanId) =>
  `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}/sections`;

const buildSectionUrl = (casePlanId, sectionId) =>
  `${buildSectionsUrl(casePlanId)}/${sectionId}`;

/**
 * Creates the placeholder section the Add Section control opens in edit mode.
 * `insertBeforeSectionId` is a required key: a section id inserts above that section,
 * `null` appends after the last active section.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {{ insertBeforeSectionId: string | null }} payload
 * @returns {Promise<object>} Created section (201), including its empty `widgets` array
 */
export const createCasePlanSection = (casePlanId, { insertBeforeSectionId }) =>
  request({
    url: buildSectionsUrl(casePlanId),
    method: 'POST',
    body: JSON.stringify({ insertBeforeSectionId: insertBeforeSectionId ?? null }),
    suppressErrorToast: true,
  });

/**
 * Saves section fields (last write wins). Send only the fields being changed;
 * the API rejects an empty body.
 *
 * Due to a config issue we had to go this route again: POST instead of PATCH.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} sectionId - UUID of the section
 * @param {{ name?: string, description?: string | null, visibleToLearner?: boolean }} fields
 * @returns {Promise<object>} Updated section, including its `widgets`
 */
export const updateCasePlanSection = (casePlanId, sectionId, fields) =>
  request({
    url: buildSectionUrl(casePlanId, sectionId),
    method: 'POST',
    body: JSON.stringify(fields),
    suppressErrorToast: true,
  });

/**
 * Reorders a section among its active siblings by placing it at the target slot.
 * `sortOrder` is the final index among active sections (`0..n-1`); matching the
 * current index is a server-side no-op.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} sectionId - UUID of the section
 * @param {number} sortOrder - Target slot among active sections
 * @returns {Promise<object>} Moved section, including its `widgets`
 */
export const moveCasePlanSection = (casePlanId, sectionId, sortOrder) =>
  request({
    url: `${buildSectionUrl(casePlanId, sectionId)}/move`,
    method: 'POST',
    body: JSON.stringify({ sortOrder }),
    suppressErrorToast: true,
  });

/**
 * Soft-deletes a section. Draft plans only; the API answers 409 when another
 * active section still depends on it.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} sectionId - UUID of the section
 * @returns {Promise<object>} Archived section, including its `widgets`
 */
export const deleteCasePlanSection = (casePlanId, sectionId) =>
  request({
    url: buildSectionUrl(casePlanId, sectionId),
    method: 'DELETE',
    suppressErrorToast: true,
  });

/**
 * Archives a section. Active and Released plans only.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} sectionId - UUID of the section
 * @returns {Promise<object>} Archived section, including its `widgets`
 */
export const archiveCasePlanSection = (casePlanId, sectionId) =>
  request({
    url: `${buildSectionUrl(casePlanId, sectionId)}/archive`,
    method: 'POST',
    suppressErrorToast: true,
  });

/** @type {{ categories: object[] } | null} */
let cachedWidgetTypes = null;

/** @type {Promise<{ categories: object[] }> | null} */
let inflightWidgetTypesRequest = null;

/**
 * Clears the in-memory widget-type catalog cache (e.g. between tests).
 */
export const clearCasePlanWidgetTypesCache = () => {
  cachedWidgetTypes = null;
  inflightWidgetTypesRequest = null;
};

/**
 * Active widget-type catalog for the Add Widget picker. Not plan-scoped.
 * Cached in memory for the session; concurrent callers share one in-flight GET.
 * @returns {Promise<{ categories: object[] }>}
 */
export const fetchCasePlanWidgetTypes = () => {
  if (cachedWidgetTypes !== null) {
    return Promise.resolve(cachedWidgetTypes);
  }

  if (inflightWidgetTypesRequest !== null) {
    return inflightWidgetTypesRequest;
  }

  inflightWidgetTypesRequest = request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plan/widget-types`,
    method: 'GET',
    suppressErrorToast: true,
  })
    .then((response) => {
      cachedWidgetTypes = response;
      inflightWidgetTypesRequest = null;
      return cachedWidgetTypes;
    })
    .catch((error) => {
      inflightWidgetTypesRequest = null;
      throw error;
    });

  return inflightWidgetTypesRequest;
};

/**
 * Creates a widget in a section. Honest Jobs types accept empty `config`.
 * Note/Checklist still fail-close (400) until validators are registered.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {{ sectionId: string, widgetType: string, config?: object }} payload
 * @returns {Promise<object>} Created widget DTO (201)
 */
export const createCasePlanWidget = (casePlanId, { sectionId, widgetType, config = {} }) =>
  request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}/widgets`,
    method: 'POST',
    body: JSON.stringify({ sectionId, widgetType, config }),
    suppressErrorToast: true,
    throw400Error: true,
  });

/**
 * Atomically replaces a widget's config (last write wins).
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} widgetId - UUID of the widget
 * @param {object} config - Full widget config to persist
 * @returns {Promise<object>} Updated widget
 */
export const updateCasePlanWidgetConfig = (casePlanId, widgetId, config) =>
  request({
    url: buildWidgetUrl(casePlanId, widgetId),
    method: 'PATCH',
    body: JSON.stringify({ config }),
    suppressErrorToast: true,
  });
